/**
 * vibecheck polish — Production-grade code quality improvement engine
 *
 * 50+ pattern checks across 10 categories:
 *   types        — as any, implicit any, missing return types, non-null assertions
 *   readability  — magic numbers, nested ternaries, long functions, deep nesting
 *   performance  — inline functions in JSX, missing memo, unnecessary re-renders
 *   naming       — boolean naming, constant casing, handler naming, single-char vars
 *   structure    — barrel exports, circular-risk, god files, mixed concerns
 *   security     — dangerouslySetInnerHTML, eval, innerHTML, unescaped user input
 *   quality      — console.log, empty catch, debugger, TODO/FIXME, dead code
 *   react        — missing keys, effect deps, error boundaries, stale closures
 *   async        — floating promises, missing try/catch, race conditions
 *   accessibility — missing alt text, click without keyboard, aria labels
 *
 * All suggestions include before/after with auto-apply support.
 */

import * as fs from 'fs';
import * as path from 'path';
import type { Command } from 'commander';
import ora from 'ora';
import type { GlobalOptions } from '../types';
import { walkFiles, walkChangedFiles, readFileContent } from '../core/file-walker';
import * as out from '../ui/output';
import { colors, icons } from '../ui/theme';

// ── Types ───────────────────────────────────────────────────────────────────

type PolishCategory = 'types' | 'readability' | 'performance' | 'naming' | 'structure' | 'security' | 'quality' | 'react' | 'async' | 'accessibility';

interface PolishSuggestion {
  line: number;
  type: PolishCategory;
  description: string;
  before: string;
  after: string;
  autoFixable: boolean;
  severity: 'info' | 'warning' | 'error';
}

interface PolishOpts extends GlobalOptions {
  mode: 'file' | 'dir' | 'diff' | 'full';
  target?: string;
  apply?: boolean;
  categories?: string;
  aggressive?: boolean;
  dryRun?: boolean;
  maxPerFile?: number;
}

interface FilePolishResult {
  file: string;
  suggestions: PolishSuggestion[];
  applied: number;
}

// ── Registration ────────────────────────────────────────────────────────────

export function registerPolish(program: Command): void {
  const cmd = program
    .command('polish')
    .description('Improve code quality with 50+ pattern checks across 10 categories')
    .option('--apply', 'Apply auto-fixable suggestions')
    .option('--categories <list>', 'Focus: types,readability,performance,naming,structure,security,quality')
    .option('--aggressive', 'Include opinionated suggestions')
    .option('--dry-run', 'Show changes without applying')
    .option('--max-per-file <n>', 'Max suggestions per file', parseInt as any);

  cmd.command('file <path>').description('Polish a single file').action(async (filePath: string, _o: any, c: Command) => {
    await runPolish({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts() as any), mode: 'file', target: filePath });
  });

  cmd.command('dir <path>').description('Polish all files in a directory').action(async (dirPath: string, _o: any, c: Command) => {
    await runPolish({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts() as any), mode: 'dir', target: dirPath });
  });

  cmd.command('diff').description('Polish only changed files (git diff)').action(async (_o: any, c: Command) => {
    await runPolish({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts() as any), mode: 'diff' });
  });

  cmd.command('full').description('Polish entire project').action(async (_o: any, c: Command) => {
    await runPolish({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts() as any), mode: 'full' });
  });

  cmd.action(async (opts: any, command: Command) => {
    // Default: polish changed files, fall back to full
    const globals = command.parent!.opts() as GlobalOptions;
    const changed = await walkChangedFiles(globals.path || process.cwd());
    const mode = changed.length > 0 ? 'diff' : 'full';
    await runPolish({ ...globals, ...opts, mode });
  });
}

// ── Main Runner ─────────────────────────────────────────────────────────────

async function runPolish(opts: PolishOpts): Promise<void> {
  const start = performance.now();
  const rootPath = opts.path || process.cwd();
  const categoryFilter = opts.categories ? new Set(opts.categories.split(',').map(c => c.trim())) as Set<PolishCategory> : null;
  const maxPerFile = opts.maxPerFile || 25;

  let files: string[];
  switch (opts.mode) {
    case 'file':
      files = opts.target ? [opts.target] : [];
      break;
    case 'dir':
      files = await walkFiles(rootPath, { include: [`${opts.target}/**/*.{ts,tsx,js,jsx}`] });
      break;
    case 'diff':
      files = await walkChangedFiles(rootPath);
      break;
    case 'full':
    default:
      files = await walkFiles(rootPath, { maxFiles: 500 });
      break;
  }

  if (files.length === 0) {
    out.brandHeader('Polish');
    out.nl();
    out.warn('No files found to polish.');
    return;
  }

  const spinner = ora({ text: `Analyzing ${files.length} file${files.length > 1 ? 's' : ''}...`, isSilent: opts.quiet }).start();

  const results: FilePolishResult[] = [];
  let totalSuggestions = 0;
  let totalApplied = 0;

  const byCat: Record<string, number> = {};
  const bySev: Record<string, number> = {};

  for (const file of files) {
    const content = readFileContent(rootPath, file);
    if (!content) continue;

    let suggestions = analyzeForPolish(content, file, !!opts.aggressive);

    // Filter by category
    if (categoryFilter) {
      suggestions = suggestions.filter(s => categoryFilter.has(s.type));
    }

    // Cap per file
    if (suggestions.length > maxPerFile) {
      suggestions = suggestions.slice(0, maxPerFile);
    }

    if (suggestions.length === 0) continue;
    totalSuggestions += suggestions.length;

    for (const s of suggestions) {
      byCat[s.type] = (byCat[s.type] || 0) + 1;
      bySev[s.severity] = (bySev[s.severity] || 0) + 1;
    }

    let applied = 0;

    // Apply auto-fixable suggestions
    if (opts.apply && !opts.dryRun) {
      const fixable = suggestions.filter(s => s.autoFixable);
      if (fixable.length > 0) {
        let modified = content;
        const lines = modified.split('\n');
        const sorted = [...fixable].sort((a, b) => b.line - a.line);
        for (const s of sorted) {
          if (s.line > 0 && s.line <= lines.length) {
            lines[s.line - 1] = lines[s.line - 1].replace(s.before.trim(), s.after.trim());
          }
        }
        modified = lines.join('\n');
        const absPath = path.resolve(rootPath, file);
        fs.writeFileSync(absPath, modified, 'utf-8');
        applied = fixable.length;
        totalApplied += applied;
      }
    }

    results.push({ file, suggestions, applied });
  }

  spinner.stop();
  const duration = performance.now() - start;

  // ── Compute health score ──────────────────────────────────────────────────

  const sevWeights: Record<string, number> = { error: 15, warning: 5, info: 1 };
  const totalDeduction = Object.entries(bySev).reduce(
    (sum, [sev, count]) => sum + (sevWeights[sev] ?? 3) * count, 0,
  );
  const healthScore = Math.max(0, Math.min(100, 100 - totalDeduction));
  const autoFixableCount = results.reduce((s, r) => s + r.suggestions.filter(x => x.autoFixable).length, 0);

  // Flatten findings for consumers that expect top-level `findings` array
  const findings = results.flatMap(r =>
    r.suggestions.map(s => ({
      id: `polish-${s.type}-L${s.line}`,
      file: r.file,
      line: s.line,
      type: s.type,
      severity: s.severity,
      message: s.description,
      description: s.description,
      autoFixable: s.autoFixable,
      howToFix: s.autoFixable ? s.after.trim().slice(0, 120) : undefined,
    })),
  );

  // ── Persist results ───────────────────────────────────────────────────────

  try {
    const vcDir = path.join(rootPath, '.vibecheck');
    if (!fs.existsSync(vcDir)) fs.mkdirSync(vcDir, { recursive: true });
    fs.writeFileSync(
      path.join(vcDir, 'polish-results.json'),
      JSON.stringify({ timestamp: new Date().toISOString(), healthScore, totalSuggestions, findings: findings.slice(0, 200) }, null, 2),
      'utf-8',
    );
  } catch { /* non-critical — skip if we can't write */ }

  // ── JSON output ───────────────────────────────────────────────────────────

  if (opts.json) {
    out.jsonOutput({
      command: 'polish',
      healthScore,
      score: healthScore,
      totalSuggestions,
      totalApplied,
      autoFixable: autoFixableCount,
      filesScanned: files.length,
      filesAnalyzed: files.length,
      filesWithSuggestions: results.length,
      byCategory: byCat,
      bySeverity: bySev,
      findings,
      results: results.map(r => ({
        file: r.file,
        suggestions: r.suggestions.length,
        applied: r.applied,
        items: r.suggestions.map(s => ({
          line: s.line,
          type: s.type,
          severity: s.severity,
          description: s.description,
          autoFixable: s.autoFixable,
        })),
      })),
      durationMs: Math.round(duration),
    });
    process.exitCode = (bySev['error'] || 0) > 0 ? 1 : 0;
    return;
  }

  // ── Pretty output ─────────────────────────────────────────────────────────

  out.brandHeader('Polish', `${totalSuggestions} improvements across ${results.length} files`);
  out.nl();

  if (totalSuggestions === 0) {
    out.success('Code looks great — no improvements needed!');
    out.nl();
    console.log(out.completedIn(duration));
    return;
  }

  // Category summary
  const catEntries = Object.entries(byCat).sort((a, b) => b[1] - a[1]);
  console.log(colors.bold('  By category:'));
  for (const [cat, count] of catEntries) {
    const icon = CAT_ICONS[cat as PolishCategory] || '  ';
    console.log(`    ${icon} ${cat}: ${colors.bold(String(count))}`);
  }
  out.nl();

  // Per-file results
  for (const result of results) {
    const appliedStr = result.applied > 0 ? colors.success(` (${result.applied} applied)`) : '';
    console.log(`  ${icons.file} ${colors.bold(result.file)} — ${result.suggestions.length} suggestion${result.suggestions.length > 1 ? 's' : ''}${appliedStr}`);

    // Group by category
    const grouped: Record<string, PolishSuggestion[]> = {};
    for (const s of result.suggestions) {
      if (!grouped[s.type]) grouped[s.type] = [];
      grouped[s.type].push(s);
    }

    for (const [cat, items] of Object.entries(grouped)) {
      for (const s of items.slice(0, 5)) {
        const sevColor = s.severity === 'error' ? colors.error : s.severity === 'warning' ? colors.warning : colors.dim;
        const fixTag = s.autoFixable ? colors.success(' [auto-fix]') : '';
        console.log(`    ${sevColor(`L${s.line}`)} ${colors.dim(`[${cat}]`)} ${s.description}${fixTag}`);
        if (opts.verbose) {
          console.log(`      ${colors.error('- ' + s.before.trim().slice(0, 90))}`);
          console.log(`      ${colors.success('+ ' + s.after.trim().slice(0, 90))}`);
        }
      }
      if (items.length > 5) {
        console.log(`    ${colors.dim(`... and ${items.length - 5} more ${cat} suggestions`)}`);
      }
    }
    out.nl();
  }

  // Score display
  const scoreColor = healthScore >= 80 ? colors.success : healthScore >= 50 ? colors.warning : colors.error;
  console.log(`  ${scoreColor(`Polish Score: ${healthScore}/100`)}`);
  out.nl();

  // Footer
  console.log(out.divider());
  const parts: string[] = [];
  if (bySev['error']) parts.push(colors.error(`${bySev['error']} errors`));
  if (bySev['warning']) parts.push(colors.warning(`${bySev['warning']} warnings`));
  if (bySev['info']) parts.push(colors.dim(`${bySev['info']} info`));
  console.log(`  ${parts.join(colors.dim(' · '))} across ${results.length} file${results.length > 1 ? 's' : ''}`);

  if (totalApplied > 0) {
    console.log(colors.success(`  ✓ Applied ${totalApplied} auto-fixes`));
  }

  if (!opts.apply && autoFixableCount > 0) {
    out.nl();
    console.log(`  ${autoFixableCount} auto-fixable. Run ${colors.info('vibecheck polish --apply')} to apply.`);
  }

  out.nl();
  console.log(out.completedIn(duration));
  process.exitCode = (bySev['error'] || 0) > 0 ? 1 : 0;
}

// ── Category Icons ──────────────────────────────────────────────────────────

const CAT_ICONS: Record<PolishCategory, string> = {
  types: '🔷',
  readability: '📖',
  performance: '⚡',
  naming: '🏷️',
  structure: '🏗️',
  security: '🔒',
  quality: '✨',
  react: '⚛️',
  async: '🔄',
  accessibility: '♿',
};

// ═══════════════════════════════════════════════════════════════════════════════
// ANALYSIS ENGINE — 30+ patterns across 7 categories
// ═══════════════════════════════════════════════════════════════════════════════

function analyzeForPolish(content: string, filePath: string, aggressive: boolean): PolishSuggestion[] {
  const suggestions: PolishSuggestion[] = [];
  const lines = content.split('\n');
  const ext = path.extname(filePath);
  if (!['.ts', '.tsx', '.js', '.jsx'].includes(ext)) return suggestions;

  const isTest = filePath.includes('.test.') || filePath.includes('.spec.') || filePath.includes('__tests__');
  const isCLI = filePath.includes('/cli/') || filePath.includes('/bin/') || filePath.includes('/commands/');
  const isConfig = filePath.includes('.config.') || filePath.includes('rc.') || filePath.endsWith('config.ts');
  const isTS = ext === '.ts' || ext === '.tsx';
  const isTSX = ext === '.tsx' || ext === '.jsx';

  // Track function length for structure checks
  let funcStartLine = -1;
  let braceDepth = 0;
  let currentNesting = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lineNum = i + 1;
    const trimmed = line.trim();

    // Skip comments and empty lines
    if (trimmed.startsWith('//') || trimmed.startsWith('*') || trimmed.startsWith('/*') || trimmed === '') continue;

    // ═══ TYPES ═══════════════════════════════════════════════════════════════

    // T01: as any
    if (/\bas\s+any\b/.test(line) && !isTest) {
      suggestions.push({
        line: lineNum, type: 'types', severity: 'warning', autoFixable: true,
        description: "Replace 'as any' with 'as unknown' for type safety",
        before: line, after: line.replace(/\bas\s+any\b/, 'as unknown'),
      });
    }

    // T02: non-null assertion (!) on member access
    if (/\w+!\.\w+/.test(line) && !isTest && aggressive) {
      suggestions.push({
        line: lineNum, type: 'types', severity: 'info', autoFixable: false,
        description: 'Non-null assertion — consider optional chaining (?.) or null check',
        before: line, after: line.replace(/(\w+)!\.(\w+)/, '$1?.$2'),
      });
    }

    // T03: @ts-ignore without explanation
    if (/\/\/\s*@ts-ignore\s*$/.test(trimmed)) {
      suggestions.push({
        line: lineNum, type: 'types', severity: 'warning', autoFixable: true,
        description: 'Add explanation to @ts-ignore — use @ts-expect-error with reason',
        before: line, after: line.replace(/@ts-ignore/, '@ts-expect-error TODO: explain why this is needed'),
      });
    }

    // T04: Function without return type (exported functions only)
    if (isTS && /^export\s+(async\s+)?function\s+\w+\s*\([^)]*\)\s*\{/.test(trimmed) && !/:/.test(trimmed.split('{')[0].split(')')[1] || '')) {
      suggestions.push({
        line: lineNum, type: 'types', severity: 'info', autoFixable: false,
        description: 'Exported function missing return type annotation',
        before: trimmed, after: trimmed.replace(/\)\s*\{/, '): ReturnType { /* TODO: add return type */'),
      });
    }

    // T05: == instead of ===
    if (/[^!=]==[^=]/.test(line) && !/===/.test(line.replace(/==/g, '')) && !isConfig) {
      const eqMatch = line.match(/([^!=])(==)([^=])/);
      if (eqMatch) {
        suggestions.push({
          line: lineNum, type: 'types', severity: 'warning', autoFixable: true,
          description: 'Use strict equality (===) instead of loose equality (==)',
          before: line, after: line.replace(/([^!=])==([^=])/, '$1===$2'),
        });
      }
    }

    // T06: `any` in function parameters
    if (isTS && /\(\s*\w+\s*:\s*any\b/.test(line) && !isTest && !isConfig) {
      suggestions.push({
        line: lineNum, type: 'types', severity: 'warning', autoFixable: false,
        description: 'Function parameter typed as `any` — use a specific type or `unknown`',
        before: trimmed.slice(0, 80), after: '// TODO: Replace `any` with a specific type or `unknown`',
      });
    }

    // T07: Type assertion chain (as X as Y)
    if (/\bas\s+\w+.*\bas\s+\w+/.test(line) && !isTest) {
      suggestions.push({
        line: lineNum, type: 'types', severity: 'warning', autoFixable: false,
        description: 'Double type assertion — indicates a type design issue',
        before: trimmed.slice(0, 80), after: '// TODO: Fix the underlying type instead of double-casting',
      });
    }

    // T08: Unnecessary type assertion on literal
    if (/\bas\s+(string|number|boolean)\b/.test(line) && /['"\d]/.test(line) && !isTest) {
      suggestions.push({
        line: lineNum, type: 'types', severity: 'info', autoFixable: false,
        description: 'Type assertion on literal — TypeScript can infer this automatically',
        before: trimmed.slice(0, 80), after: '// TODO: Remove unnecessary type assertion — let TS infer',
      });
    }

    // ═══ READABILITY ═════════════════════════════════════════════════════════

    // R01: Magic numbers (4+ digits)
    const magicMatch = trimmed.match(/(?:===?|!==?|[<>]=?|[+\-*/])\s*(\d{4,})\b/);
    if (magicMatch && !trimmed.startsWith('//') && !trimmed.startsWith('*') && !isConfig) {
      suggestions.push({
        line: lineNum, type: 'readability', severity: 'info', autoFixable: false,
        description: `Extract magic number ${magicMatch[1]} into a named constant`,
        before: line, after: `// TODO: const MEANINGFUL_NAME = ${magicMatch[1]};`,
      });
    }

    // R02: Nested ternary
    if (/\?.*\?.*:.*:/.test(line) && line.length > 60) {
      suggestions.push({
        line: lineNum, type: 'readability', severity: 'warning', autoFixable: false,
        description: 'Nested ternary — extract to helper function or use if/else',
        before: trimmed.slice(0, 80), after: '// TODO: Extract nested ternary to a helper function',
      });
    }

    // R03: Line too long (>120 chars)
    if (line.length > 120 && !line.includes('http') && !line.includes('import') && aggressive) {
      suggestions.push({
        line: lineNum, type: 'readability', severity: 'info', autoFixable: false,
        description: `Line is ${line.length} chars — consider breaking into multiple lines`,
        before: trimmed.slice(0, 80) + '...', after: '// TODO: Break into multiple lines',
      });
    }

    // R04: Complex boolean expression (3+ conditions)
    if (/&&.*&&.*&&|(\|\|.*){3,}/.test(line)) {
      suggestions.push({
        line: lineNum, type: 'readability', severity: 'info', autoFixable: false,
        description: 'Complex boolean expression — extract conditions into named variables',
        before: trimmed.slice(0, 80), after: '// TODO: const isConditionMet = ...',
      });
    }

    // R05: String concatenation with + (prefer template literals)
    if (/['"].*\+.*['"]/.test(line) && !line.includes('`') && isTS && aggressive) {
      suggestions.push({
        line: lineNum, type: 'readability', severity: 'info', autoFixable: false,
        description: 'Use template literals instead of string concatenation',
        before: trimmed.slice(0, 80), after: '// TODO: Convert to template literal `${...}`',
      });
    }

    // R06: Callback nesting (callback hell)
    if (/\)\s*=>\s*\{/.test(line) && /\)\s*=>\s*\{/.test(lines[Math.min(i + 1, lines.length - 1)] || '')) {
      suggestions.push({
        line: lineNum, type: 'readability', severity: 'warning', autoFixable: false,
        description: 'Nested callbacks — flatten with async/await or extract named functions',
        before: trimmed.slice(0, 80), after: '// TODO: Refactor nested callbacks using async/await',
      });
    }

    // R07: Multiple returns in a short function (< 10 lines)
    if (/^\s*return\b/.test(line) && !isTest) {
      let returnCount = 0;
      const scanStart = Math.max(0, i - 10);
      for (let k = scanStart; k <= i; k++) {
        if (/^\s*return\b/.test(lines[k])) returnCount++;
      }
      if (returnCount >= 3) {
        suggestions.push({
          line: lineNum, type: 'readability', severity: 'info', autoFixable: false,
          description: 'Multiple return statements in close proximity — consider simplifying control flow',
          before: trimmed.slice(0, 80), after: '// TODO: Simplify to fewer return paths',
        });
      }
    }

    // ═══ PERFORMANCE ═════════════════════════════════════════════════════════

    // P01: Inline arrow function in JSX event handler
    if (isTSX && /\b(onClick|onChange|onSubmit|onBlur|onFocus|onKeyDown|onKeyUp|onMouseEnter|onMouseLeave)\s*=\s*\{.*=>/.test(line)) {
      suggestions.push({
        line: lineNum, type: 'performance', severity: 'info', autoFixable: false,
        description: 'Inline function in JSX prop — extract to useCallback or named function',
        before: trimmed.slice(0, 80), after: '// TODO: const handleClick = useCallback(() => { ... }, [deps])',
      });
    }

    // P02: new RegExp in loop or render
    if (/new\s+RegExp\s*\(/.test(line) && !line.includes('const') && !line.includes('let')) {
      suggestions.push({
        line: lineNum, type: 'performance', severity: 'info', autoFixable: false,
        description: 'RegExp created inline — hoist to module scope or memoize',
        before: trimmed.slice(0, 80), after: '// TODO: const MY_REGEX = new RegExp(...) at module scope',
      });
    }

    // P03: JSON.parse(JSON.stringify()) for deep clone
    if (/JSON\.parse\s*\(\s*JSON\.stringify\s*\(/.test(line)) {
      suggestions.push({
        line: lineNum, type: 'performance', severity: 'warning', autoFixable: true,
        description: 'Use structuredClone() instead of JSON.parse(JSON.stringify())',
        before: line, after: line.replace(/JSON\.parse\s*\(\s*JSON\.stringify\s*\(([^)]+)\)\s*\)/, 'structuredClone($1)'),
      });
    }

    // P04: Array.indexOf !== -1 (prefer .includes)
    if (/\.indexOf\s*\([^)]+\)\s*(!==?|>=)\s*-1/.test(line)) {
      suggestions.push({
        line: lineNum, type: 'performance', severity: 'info', autoFixable: false,
        description: 'Use .includes() instead of .indexOf() !== -1',
        before: trimmed.slice(0, 80), after: '// TODO: arr.includes(value)',
      });
    }

    // P05: Spread in reduce (O(n²) copy)
    if (/\.reduce\s*\(.*\{.*\.\.\./.test(line) && aggressive) {
      suggestions.push({
        line: lineNum, type: 'performance', severity: 'warning', autoFixable: false,
        description: 'Spread inside reduce creates O(n²) copies — use mutation or Map',
        before: trimmed.slice(0, 80), after: '// TODO: Use Object.assign or Map for accumulator',
      });
    }

    // P06: Object.keys().forEach → for...of with Object.entries
    if (/Object\.keys\s*\([^)]+\)\.forEach/.test(line)) {
      suggestions.push({
        line: lineNum, type: 'performance', severity: 'info', autoFixable: false,
        description: 'Use for...of with Object.entries() instead of Object.keys().forEach()',
        before: trimmed.slice(0, 80), after: '// TODO: for (const [key, value] of Object.entries(obj)) { ... }',
      });
    }

    // P07: Synchronous fs operations in non-config files
    if (/\bfs\.(readFileSync|writeFileSync|existsSync|mkdirSync)\b/.test(line) && !isConfig && !isCLI) {
      suggestions.push({
        line: lineNum, type: 'performance', severity: 'info', autoFixable: false,
        description: 'Synchronous fs operation — use async variant to avoid blocking',
        before: trimmed.slice(0, 80), after: '// TODO: Use fs.promises.readFile() or fs/promises',
      });
    }

    // P08: Creating objects/arrays inside render (new Map/Set/Array on every render)
    if (isTSX && /(?:new\s+(?:Map|Set|Array|Object)\s*\(|Array\.from\s*\()/.test(line) && !/(?:const|let|var)\s+\w+\s*=/.test(line)) {
      suggestions.push({
        line: lineNum, type: 'performance', severity: 'info', autoFixable: false,
        description: 'Object/collection created inline — hoist to useMemo or module scope',
        before: trimmed.slice(0, 80), after: '// TODO: const memoized = useMemo(() => new Map(...), [deps])',
      });
    }

    // ═══ NAMING ══════════════════════════════════════════════════════════════

    // N01: Boolean variable without is/has/should/can prefix
    const boolMatch = line.match(/(?:const|let)\s+([a-z]\w*)\s*(?::\s*boolean)?\s*=\s*(true|false)\b/);
    if (boolMatch && !/^(is|has|should|can|will|did|was|are|do|need)/.test(boolMatch[1]) && aggressive) {
      const name = boolMatch[1];
      suggestions.push({
        line: lineNum, type: 'naming', severity: 'info', autoFixable: false,
        description: `Boolean '${name}' should start with is/has/should/can`,
        before: trimmed, after: trimmed.replace(name, `is${name.charAt(0).toUpperCase()}${name.slice(1)}`),
      });
    }

    // N02: SCREAMING_SNAKE for non-constant
    if (/(?:let|var)\s+[A-Z][A-Z_0-9]+\s*=/.test(line)) {
      suggestions.push({
        line: lineNum, type: 'naming', severity: 'warning', autoFixable: false,
        description: 'SCREAMING_SNAKE_CASE should only be used for const declarations',
        before: trimmed.slice(0, 80), after: '// TODO: Use camelCase for let/var, or change to const',
      });
    }

    // N03: Single-character variable (except i, j, k, e, _, x, y)
    const singleCharMatch = line.match(/(?:const|let|var)\s+([a-df-hln-wz])\s*[=:]/);
    if (singleCharMatch && !isTest) {
      suggestions.push({
        line: lineNum, type: 'naming', severity: 'info', autoFixable: false,
        description: `Single-character variable '${singleCharMatch[1]}' — use a descriptive name`,
        before: trimmed.slice(0, 80), after: `// TODO: Rename '${singleCharMatch[1]}' to something descriptive`,
      });
    }

    // N04: Handler not prefixed with handle/on
    const handlerMatch = line.match(/(?:const|function)\s+(\w+)\s*=?\s*(?:\([^)]*\))?\s*(?:=>)?\s*\{/);
    if (handlerMatch && /click|submit|change|press|focus|blur|hover|select/i.test(handlerMatch[1]) && !/^(handle|on)/.test(handlerMatch[1]) && isTSX) {
      suggestions.push({
        line: lineNum, type: 'naming', severity: 'info', autoFixable: false,
        description: `Event handler '${handlerMatch[1]}' should be prefixed with 'handle' or 'on'`,
        before: trimmed.slice(0, 80), after: `// TODO: Rename to handle${handlerMatch[1].charAt(0).toUpperCase()}${handlerMatch[1].slice(1)}`,
      });
    }

    // ═══ SECURITY ════════════════════════════════════════════════════════════

    // S01: dangerouslySetInnerHTML
    if (/dangerouslySetInnerHTML/.test(line)) {
      suggestions.push({
        line: lineNum, type: 'security', severity: 'error', autoFixable: false,
        description: 'dangerouslySetInnerHTML — XSS risk. Sanitize with DOMPurify or use safe alternatives',
        before: trimmed.slice(0, 80), after: '// TODO: Use DOMPurify.sanitize() or a safe rendering library',
      });
    }

    // S02: eval()
    if (/\beval\s*\(/.test(line) && !isTest) {
      suggestions.push({
        line: lineNum, type: 'security', severity: 'error', autoFixable: false,
        description: 'eval() is a critical security risk — use safer alternatives',
        before: trimmed.slice(0, 80), after: '// TODO: Replace eval() with JSON.parse(), Function(), or a proper parser',
      });
    }

    // S03: innerHTML assignment
    if (/\.innerHTML\s*=/.test(line) && !isTest) {
      suggestions.push({
        line: lineNum, type: 'security', severity: 'error', autoFixable: false,
        description: 'Direct innerHTML assignment — XSS risk. Use textContent or sanitize',
        before: trimmed.slice(0, 80), after: '// TODO: Use .textContent = ... or sanitize input',
      });
    }

    // S04: document.write
    if (/document\.write\s*\(/.test(line)) {
      suggestions.push({
        line: lineNum, type: 'security', severity: 'error', autoFixable: false,
        description: 'document.write() is dangerous and blocks parsing — use DOM APIs',
        before: trimmed.slice(0, 80), after: '// TODO: Use document.createElement() or React rendering',
      });
    }

    // S05: Hardcoded secret patterns
    if (/(?:password|secret|api_?key|token|auth)\s*[:=]\s*['"][^'"]{8,}['"]/i.test(line) && !isTest && !isConfig) {
      suggestions.push({
        line: lineNum, type: 'security', severity: 'error', autoFixable: false,
        description: 'Possible hardcoded secret — move to environment variable',
        before: trimmed.slice(0, 60) + '...', after: '// TODO: Use process.env.SECRET_NAME instead',
      });
    }

    // S06: SQL injection risk (template literal or concat in query)
    if (/(?:query|execute|sql)\s*\(\s*`/.test(line) && /\$\{/.test(line) && !isTest) {
      suggestions.push({
        line: lineNum, type: 'security', severity: 'error', autoFixable: false,
        description: 'SQL injection risk — use parameterized queries instead of string interpolation',
        before: trimmed.slice(0, 80), after: '// TODO: Use parameterized queries: query("SELECT * WHERE id = $1", [id])',
      });
    }

    // S07: Unvalidated redirect
    if (/(?:window\.location|res\.redirect)\s*[=(]\s*(?:req\.|params\.|query\.)/.test(line) && !isTest) {
      suggestions.push({
        line: lineNum, type: 'security', severity: 'warning', autoFixable: false,
        description: 'Unvalidated redirect — validate URL against allowlist before redirecting',
        before: trimmed.slice(0, 80), after: '// TODO: Validate redirect URL against an allowlist',
      });
    }

    // ═══ QUALITY ═════════════════════════════════════════════════════════════

    // Q01: console.log in production code
    if (/^\s*console\.log\s*\(/.test(line) && !isTest && !isCLI) {
      suggestions.push({
        line: lineNum, type: 'quality', severity: 'warning', autoFixable: true,
        description: 'Remove console.log from production code',
        before: line, after: line.replace(/console\.log/, '// console.log'),
      });
    }

    // Q02: Empty catch block
    if (/catch\s*\([^)]*\)\s*\{\s*\}/.test(line)) {
      suggestions.push({
        line: lineNum, type: 'quality', severity: 'warning', autoFixable: true,
        description: 'Empty catch block swallows errors — add logging or re-throw',
        before: line, after: line.replace(/\{\s*\}/, '{ /* TODO: handle error */ }'),
      });
    }

    // Q03: debugger statement
    if (/^\s*debugger\s*;?\s*$/.test(line)) {
      suggestions.push({
        line: lineNum, type: 'quality', severity: 'error', autoFixable: true,
        description: 'Remove debugger statement before shipping',
        before: line, after: line.replace(/debugger\s*;?/, '// debugger (removed)'),
      });
    }

    // Q04: TODO/FIXME/HACK/XXX markers
    if (/\/\/\s*(TODO|FIXME|HACK|XXX)\b/i.test(line) && aggressive) {
      const marker = line.match(/\/\/\s*(TODO|FIXME|HACK|XXX)/i)?.[1] || 'TODO';
      suggestions.push({
        line: lineNum, type: 'quality', severity: marker === 'FIXME' || marker === 'HACK' ? 'warning' : 'info', autoFixable: false,
        description: `${marker} marker — resolve before shipping`,
        before: trimmed.slice(0, 80), after: `// TODO: Resolve this ${marker}`,
      });
    }

    // Q05: console.error/warn without context
    if (/console\.(error|warn)\s*\(\s*['"`]/.test(line) && !isTest && !isCLI && aggressive) {
      suggestions.push({
        line: lineNum, type: 'quality', severity: 'info', autoFixable: false,
        description: 'Add error context — include the error object, not just a string',
        before: trimmed.slice(0, 80), after: '// TODO: console.error("message", error) — pass the error object',
      });
    }

    // Q06: Unused variable pattern (const x = ... with no further reference)
    // Simple heuristic: variable assigned but name appears only once in file
    const unusedMatch = trimmed.match(/^(?:const|let)\s+(\w+)\s*=/);
    if (unusedMatch && !isTest && aggressive) {
      const varName = unusedMatch[1];
      // Count occurrences (simple word boundary check)
      const regex = new RegExp(`\\b${varName}\\b`, 'g');
      const occurrences = (content.match(regex) || []).length;
      if (occurrences === 1 && varName.length > 1 && varName !== '_') {
        suggestions.push({
          line: lineNum, type: 'quality', severity: 'warning', autoFixable: false,
          description: `'${varName}' appears to be unused — remove or prefix with _`,
          before: trimmed.slice(0, 80), after: `// TODO: Remove unused variable '${varName}' or prefix with _`,
        });
      }
    }

    // Q07: Promise without await or .catch
    if (/(?:new\s+Promise|\.then\s*\()/.test(line) && !/await|\.catch|\.finally/.test(line) && !isTest) {
      const nextLines = lines.slice(i, Math.min(i + 3, lines.length)).join(' ');
      if (!/\.catch|\.finally|await/.test(nextLines)) {
        suggestions.push({
          line: lineNum, type: 'quality', severity: 'warning', autoFixable: false,
          description: 'Promise without error handling — add .catch() or use try/await',
          before: trimmed.slice(0, 80), after: '// TODO: Add .catch() or wrap in try/catch with await',
        });
      }
    }

    // Q08: Commented-out code blocks (3+ consecutive commented lines)
    if (/^\s*\/\/\s*[a-zA-Z]/.test(line) && !(/^\s*\/\/\s*(TODO|FIXME|HACK|NOTE|WARN|eslint|prettier|@ts)/i.test(line)) && !isTest) {
      let commentRun = 0;
      for (let k = i; k < Math.min(i + 5, lines.length); k++) {
        if (/^\s*\/\/\s*[a-zA-Z({]/.test(lines[k])) commentRun++;
        else break;
      }
      if (commentRun >= 3) {
        suggestions.push({
          line: lineNum, type: 'quality', severity: 'info', autoFixable: false,
          description: 'Block of commented-out code — remove it (use git history to recover)',
          before: trimmed.slice(0, 80), after: '// TODO: Remove commented-out code block',
        });
      }
    }

    // Q09: Unreachable code after return/throw
    if (/^\s*(?:return|throw)\b/.test(line) && !isTest) {
      const nextIdx = i + 1;
      if (nextIdx < lines.length) {
        const nextTrimmed = lines[nextIdx].trim();
        if (nextTrimmed && !nextTrimmed.startsWith('}') && !nextTrimmed.startsWith('//') && !nextTrimmed.startsWith('*') && !nextTrimmed.startsWith('case ') && !nextTrimmed.startsWith('default:')) {
          suggestions.push({
            line: nextIdx + 1, type: 'quality', severity: 'warning', autoFixable: false,
            description: 'Unreachable code after return/throw statement',
            before: nextTrimmed.slice(0, 80), after: '// TODO: Remove unreachable code',
          });
        }
      }
    }

    // Q10: Hardcoded timeout values
    if (/setTimeout\s*\(\s*[^,]+,\s*\d{4,}\s*\)/.test(line) && !isTest) {
      const timeoutMatch = line.match(/setTimeout\s*\([^,]+,\s*(\d+)\s*\)/);
      if (timeoutMatch) {
        suggestions.push({
          line: lineNum, type: 'quality', severity: 'info', autoFixable: false,
          description: `Hardcoded timeout (${timeoutMatch[1]}ms) — extract to named constant`,
          before: trimmed.slice(0, 80), after: '// TODO: const TIMEOUT_MS = ...; setTimeout(fn, TIMEOUT_MS)',
        });
      }
    }

    // ═══ REACT ═══════════════════════════════════════════════════════════════

    // RC01: Missing key in array .map() rendering
    if (isTSX && /\.map\s*\(/.test(line)) {
      const mapBlock = lines.slice(i, Math.min(i + 5, lines.length)).join(' ');
      if (/<\w/.test(mapBlock) && !/key\s*=/.test(mapBlock)) {
        suggestions.push({
          line: lineNum, type: 'react', severity: 'error', autoFixable: false,
          description: 'Missing `key` prop in .map() — causes reconciliation bugs and poor performance',
          before: trimmed.slice(0, 80), after: '// TODO: Add key={uniqueId} to the rendered element',
        });
      }
    }

    // RC02: useEffect without dependency array
    if (/useEffect\s*\(\s*(?:\(\)|[a-zA-Z_]\w*|\([^)]*\))\s*=>\s*\{/.test(line)) {
      const effectBlock = lines.slice(i, Math.min(i + 10, lines.length)).join('\n');
      const closingMatch = effectBlock.match(/\}\s*\)\s*;/);
      if (closingMatch && !/\}\s*,\s*\[/.test(effectBlock)) {
        suggestions.push({
          line: lineNum, type: 'react', severity: 'warning', autoFixable: false,
          description: 'useEffect without dependency array — runs on every render',
          before: trimmed.slice(0, 80), after: '// TODO: Add dependency array: useEffect(() => { ... }, [deps])',
        });
      }
    }

    // RC03: setState in useEffect without cleanup (potential memory leak)
    if (/useEffect/.test(line) && !isTest) {
      const effectBlock = lines.slice(i, Math.min(i + 15, lines.length)).join(' ');
      if (/set[A-Z]\w*\s*\(/.test(effectBlock) && !/return\s*\(\s*\)\s*=>/.test(effectBlock) && /(?:fetch|setTimeout|setInterval|subscribe|addEventListener)/.test(effectBlock)) {
        suggestions.push({
          line: lineNum, type: 'react', severity: 'warning', autoFixable: false,
          description: 'useEffect with async operation and setState but no cleanup — potential memory leak',
          before: trimmed.slice(0, 80), after: '// TODO: Return cleanup function to cancel async operation',
        });
      }
    }

    // RC04: Direct state mutation
    if (/\bthis\.state\.\w+\s*=/.test(line) && !isTest) {
      suggestions.push({
        line: lineNum, type: 'react', severity: 'error', autoFixable: false,
        description: 'Direct state mutation — use setState() or useState setter',
        before: trimmed.slice(0, 80), after: '// TODO: Use this.setState({ key: value }) instead',
      });
    }

    // RC05: Spreading props without rest extraction
    if (isTSX && /\{\.\.\.props\}/.test(line) && !isTest) {
      suggestions.push({
        line: lineNum, type: 'react', severity: 'info', autoFixable: false,
        description: 'Spreading all props — extract known props and forward rest explicitly',
        before: trimmed.slice(0, 80), after: '// TODO: const { known, ...rest } = props; then {...rest}',
      });
    }

    // ═══ ASYNC ═══════════════════════════════════════════════════════════════

    // AS01: Async function that never awaits
    if (/^\s*(?:export\s+)?async\s+(?:function|\w+\s*=\s*async)/.test(line) && !isTest) {
      const funcBody = lines.slice(i, Math.min(i + 20, lines.length)).join(' ');
      const awaitCount = (funcBody.match(/\bawait\b/g) || []).length;
      if (awaitCount === 0 && funcBody.includes('}')) {
        suggestions.push({
          line: lineNum, type: 'async', severity: 'warning', autoFixable: false,
          description: 'Async function without await — remove async keyword or add await',
          before: trimmed.slice(0, 80), after: '// TODO: Remove async if not needed, or add await for async operations',
        });
      }
    }

    // AS02: await in a loop (potential N+1 queries)
    if (/\bawait\b/.test(line) && !isTest) {
      for (let k = Math.max(0, i - 3); k < i; k++) {
        if (/\bfor\s*\(|\bwhile\s*\(|\.forEach\s*\(/.test(lines[k])) {
          suggestions.push({
            line: lineNum, type: 'async', severity: 'warning', autoFixable: false,
            description: 'Await inside loop — use Promise.all() for parallel execution',
            before: trimmed.slice(0, 80), after: '// TODO: Collect promises and use await Promise.all([...])',
          });
          break;
        }
      }
    }

    // AS03: Promise constructor anti-pattern (wrapping async in new Promise)
    if (/new\s+Promise\s*\(\s*async/.test(line)) {
      suggestions.push({
        line: lineNum, type: 'async', severity: 'warning', autoFixable: false,
        description: 'Promise constructor anti-pattern — async functions already return promises',
        before: trimmed.slice(0, 80), after: '// TODO: Remove new Promise() wrapper — just use async function directly',
      });
    }

    // AS04: setTimeout/setInterval without cleanup ref
    if (/(?:setTimeout|setInterval)\s*\(/.test(line) && isTSX && !isTest) {
      const context = lines.slice(Math.max(0, i - 3), i + 1).join(' ');
      if (!/(?:const|let|var)\s+\w+\s*=\s*(?:setTimeout|setInterval)/.test(context) && !/clearTimeout|clearInterval/.test(lines.slice(i, Math.min(i + 5, lines.length)).join(' '))) {
        suggestions.push({
          line: lineNum, type: 'async', severity: 'warning', autoFixable: false,
          description: 'Timer without cleanup — store ref and clear in useEffect cleanup',
          before: trimmed.slice(0, 80), after: '// TODO: const ref = setTimeout(...); return () => clearTimeout(ref);',
        });
      }
    }

    // ═══ ACCESSIBILITY ═══════════════════════════════════════════════════════

    // A01: Image without alt text
    if (isTSX && /<img\b/.test(line) && !/alt\s*=/.test(line)) {
      const imgBlock = lines.slice(i, Math.min(i + 3, lines.length)).join(' ');
      if (!/alt\s*=/.test(imgBlock)) {
        suggestions.push({
          line: lineNum, type: 'accessibility', severity: 'error', autoFixable: false,
          description: 'Image missing alt text — required for screen readers (WCAG 1.1.1)',
          before: trimmed.slice(0, 80), after: '// TODO: Add alt="descriptive text" or alt="" for decorative images',
        });
      }
    }

    // A02: Click handler without keyboard handler
    if (isTSX && /onClick\s*=/.test(line) && !/onKeyDown|onKeyUp|onKeyPress|button|<a\s|<input|<select|role=/.test(line)) {
      const elemBlock = lines.slice(i, Math.min(i + 3, lines.length)).join(' ');
      if (!/onKeyDown|onKeyUp|role=|tabIndex/.test(elemBlock)) {
        suggestions.push({
          line: lineNum, type: 'accessibility', severity: 'warning', autoFixable: false,
          description: 'onClick without keyboard handler — add onKeyDown and role for non-interactive elements',
          before: trimmed.slice(0, 80), after: '// TODO: Add role="button" tabIndex={0} onKeyDown={handleKeyDown}',
        });
      }
    }

    // A03: Icon-only button without aria-label
    if (isTSX && /<button\b/.test(line)) {
      const btnBlock = lines.slice(i, Math.min(i + 4, lines.length)).join(' ');
      if (!/aria-label|aria-labelledby|title=/.test(btnBlock) && /<svg|<icon|<img/.test(btnBlock) && !/>\s*\w{2,}\s*</.test(btnBlock)) {
        suggestions.push({
          line: lineNum, type: 'accessibility', severity: 'warning', autoFixable: false,
          description: 'Icon-only button without aria-label — screen readers cannot describe its purpose',
          before: trimmed.slice(0, 80), after: '// TODO: Add aria-label="description" to the button',
        });
      }
    }

    // A04: Form input without associated label
    if (isTSX && /<input\b/.test(line) && !/type\s*=\s*['"]hidden['"]/.test(line)) {
      const inputBlock = lines.slice(i, Math.min(i + 3, lines.length)).join(' ');
      if (!/aria-label|aria-labelledby|id\s*=/.test(inputBlock)) {
        suggestions.push({
          line: lineNum, type: 'accessibility', severity: 'info', autoFixable: false,
          description: 'Input without label — add aria-label or associate with a <label> element',
          before: trimmed.slice(0, 80), after: '// TODO: Add aria-label="..." or <label htmlFor="inputId">',
        });
      }
    }

    // ═══ STRUCTURE ════════════════════════════════════════════════════════════

    // Track function length
    if (/(?:function\s+\w+|(?:const|let)\s+\w+\s*=\s*(?:async\s+)?\()/.test(trimmed) || /(?:async\s+)?(?:function|\()/.test(trimmed)) {
      if (funcStartLine === -1) funcStartLine = i;
    }

    // Track brace depth for nesting
    for (const ch of line) {
      if (ch === '{') { braceDepth++; currentNesting = Math.max(currentNesting, braceDepth); }
      if (ch === '}') {
        braceDepth--;
        if (braceDepth === 0 && funcStartLine >= 0) {
          const funcLength = i - funcStartLine;
          // ST01: Long function (>50 lines)
          if (funcLength > 50 && !isTest && !isConfig) {
            suggestions.push({
              line: funcStartLine + 1, type: 'structure', severity: 'warning', autoFixable: false,
              description: `Function is ${funcLength} lines — consider breaking into smaller functions`,
              before: lines[funcStartLine].trim().slice(0, 80), after: '// TODO: Extract logic into smaller, focused functions',
            });
          }
          // ST02: Deep nesting (>4 levels)
          if (currentNesting > 4 && !isTest) {
            suggestions.push({
              line: funcStartLine + 1, type: 'structure', severity: 'warning', autoFixable: false,
              description: `Nesting depth of ${currentNesting} — use early returns or extract functions`,
              before: lines[funcStartLine].trim().slice(0, 80), after: '// TODO: Use guard clauses (early returns) to reduce nesting',
            });
          }
          funcStartLine = -1;
          currentNesting = 0;
        }
      }
    }
  }

  // ═══ FILE-LEVEL CHECKS ═══════════════════════════════════════════════════

  // ST03: God file (>500 lines)
  if (lines.length > 500 && !isTest && !isConfig) {
    suggestions.push({
      line: 1, type: 'structure', severity: 'warning', autoFixable: false,
      description: `File is ${lines.length} lines — consider splitting into smaller modules`,
      before: path.basename(filePath), after: '// TODO: Split into focused modules',
    });
  }

  // ST04: Too many imports (>15)
  const importCount = lines.filter(l => /^\s*import\s/.test(l)).length;
  if (importCount > 15 && !isTest) {
    suggestions.push({
      line: 1, type: 'structure', severity: 'info', autoFixable: false,
      description: `${importCount} imports — consider consolidating or splitting the module`,
      before: `${importCount} import statements`, after: '// TODO: Group related imports or split module',
    });
  }

  // ST05: Mixed default and named exports
  const hasDefaultExport = lines.some(l => /^\s*export\s+default\b/.test(l));
  const namedExportCount = lines.filter(l => /^\s*export\s+(?:const|function|class|interface|type|enum|let|var)\b/.test(l)).length;
  if (hasDefaultExport && namedExportCount > 3 && !isConfig) {
    suggestions.push({
      line: 1, type: 'structure', severity: 'info', autoFixable: false,
      description: 'Mixing default export with many named exports — pick one pattern',
      before: `1 default + ${namedExportCount} named exports`, after: '// TODO: Use only named exports for better tree-shaking',
    });
  }

  // ST06: Index file with substantial logic
  if (/index\.(ts|tsx|js|jsx)$/.test(filePath) && !isConfig) {
    const codeLines = lines.filter(l => {
      const t = l.trim();
      return t && !t.startsWith('//') && !t.startsWith('*') && !t.startsWith('/*') && !t.startsWith('import') && !t.startsWith('export {') && !t.startsWith('export *') && !t.startsWith('export type');
    });
    if (codeLines.length > 30) {
      suggestions.push({
        line: 1, type: 'structure', severity: 'info', autoFixable: false,
        description: 'Index file contains substantial logic — should only re-export',
        before: path.basename(filePath), after: '// TODO: Move logic to a named module and re-export from index',
      });
    }
  }

  return suggestions;
}
